import Plugin from '../core/Plugin';
import * as SelectionUtils from '../SelectionUtils';

/**
 * FontFamilyPlugin - Provides font family selection via dropdown
 */
class FontFamilyPlugin extends Plugin {
  constructor(editor, options = {}) {
    super(editor, options);
    this.name = 'FontFamily';
  }

  getToolbarButtons() {
    return [
      {
        name: 'font-family',
        dropdown: {
          command: 'font-family',
          label: 'Font Family',
          title: 'Select Font Family',
          options: [
            { value: 'arial', label: 'Arial' },
            { value: 'times', label: 'Times New Roman' },
            { value: 'georgia', label: 'Georgia' },
            { value: 'verdana', label: 'Verdana' },
            { value: 'courier', label: 'Courier New' },
            { value: 'comic-sans', label: 'Comic Sans MS' },
            { value: 'impact', label: 'Impact' },
            { value: 'trebuchet', label: 'Trebuchet MS' },
            { value: 'lucida', label: 'Lucida Sans' },
            { value: 'proxima-nova', label: 'Proxima Nova' },
            { value: 'bree-serif', label: 'Bree Serif' },
          ],
        },
      },
    ];
  }

  /**
   * Apply font family to math elements (KaTeX and MathML) within a container
   * @param {Element} container - The container element to search within
   * @param {string} fontFamily - The font family to apply (e.g., 'Arial, Helvetica, sans-serif')
   */
  applyFontFamilyToMathElements(container, fontFamily) {
    if (!container) return;

    // Find all math elements (both KaTeX and MathML)
    const mathElements = container.querySelectorAll('.quillkit-math, .quillkit-mathml');
    mathElements.forEach((mathEl) => {
      // Apply font-family directly to the math wrapper element
      if (mathEl && mathEl.style) {
        mathEl.style.fontFamily = fontFamily;
      }
    });
  }

  executeCommand(command) {
    // Maximum DOM tree depth to traverse when checking for math elements
    // Prevents infinite loops in malformed DOM and limits performance impact
    const MAX_DOM_DEPTH = 20;
    // Map of commands to font families
    const fontFamilyMap = {
      'font-family-arial': 'Arial, Helvetica, sans-serif',
      'font-family-times': '"Times New Roman", Times, serif',
      'font-family-georgia': 'Georgia, serif',
      'font-family-verdana': 'Verdana, Geneva, sans-serif',
      'font-family-courier': '"Courier New", Courier, monospace',
      'font-family-comic-sans': '"Comic Sans MS", cursive, sans-serif',
      'font-family-impact': 'Impact, Charcoal, sans-serif',
      'font-family-trebuchet': '"Trebuchet MS", Helvetica, sans-serif',
      'font-family-lucida': '"Lucida Sans Unicode", "Lucida Grande", sans-serif',
      'font-family-proxima-nova': '"proxima-nova", "Proxima Nova", Arial, sans-serif',
      'font-family-bree-serif': '"Bree Serif", serif',
    };

    const fontFamily = fontFamilyMap[command];
    if (!fontFamily) return;

    const selection = window.getSelection();
    if (!selection || selection.rangeCount === 0) {
      return;
    }

    const range = selection.getRangeAt(0);

    // Helper function to get element from node
    const getElementNode = (node) => {
      return node.nodeType === Node.ELEMENT_NODE ? node : node.parentElement;
    };

    // Helper function to check if an element is or contains math
    const hasMathContent = (element) => {
      if (!element) return false;

      // Check if element itself is a math element
      if (element.classList?.contains('quillkit-math') || element.tagName === 'MATH') {
        return true;
      }

      // Check if element contains math elements
      if (
        element.querySelector &&
        (element.querySelector('.quillkit-math') !== null ||
          element.querySelector('math') !== null)
      ) {
        return true;
      }

      // Walk up DOM tree to check if we're inside a math element
      let node = element;
      let depth = 0;
      while (node && node !== document.body && depth < MAX_DOM_DEPTH) {
        if (node.classList?.contains('quillkit-math') || node.tagName === 'MATH') {
          return true;
        }
        node = node.parentElement;
        depth++;
      }

      return false;
    };

    const containerElement = getElementNode(range.commonAncestorContainer);

    // If selection contains math elements, handle them specially
    if (hasMathContent(containerElement)) {
      SelectionUtils.wrapSelection('span', { style: `font-family: ${fontFamily};` });

      // After wrapping, apply font family directly to math elements
      const selectionAfter = window.getSelection();
      if (selectionAfter && selectionAfter.rangeCount > 0) {
        const newRange = selectionAfter.getRangeAt(0);
        let node = newRange.commonAncestorContainer;
        if (node.nodeType !== Node.ELEMENT_NODE) {
          node = node.parentElement;
        }

        // Apply font family to math elements in the selection
        this.applyFontFamilyToMathElements(node, fontFamily);
      }
    } else {
      // For regular text, use standard wrapSelection
      SelectionUtils.wrapSelection('span', { style: `font-family: ${fontFamily};` });
    }
  }

  getMetadata() {
    return {
      name: 'Font Family',
      version: '1.0.0',
      description: 'Provides font family selection via dropdown',
      author: 'AtlaraFirm',
    };
  }
}

export default FontFamilyPlugin;
